import ReactDOM from 'react-dom/client'
import App from './containers/App/App'
import {safeInternalPath} from '@shared/utils/safeRedirect'
import './index.css'

// Capture the original URL for auth redirection
const captureInitialUrl = (): void => {
  // Skip capture for auth callback page
  if (
    window.location.pathname === '/authorize-callback' ||
    window.location.pathname === '/authentication/code/callback/'
  ) {
    return
  }

  // Store the current URL without auth-related parameters. The URL is attacker-
  // influencable via a crafted link, and this value is later used as a redirect
  // target, so only a same-origin path is ever persisted (AB#4528).
  const originalUrl = window.location.pathname + window.location.search + window.location.hash
  sessionStorage.setItem('originalUrl', safeInternalPath(originalUrl))
}

captureInitialUrl()

const main = async (): Promise<void> => {
  // No-backend mock mode (dev only): start MSW before the app mounts so the first API calls are
  // already intercepted. The import is dynamic AND gated on VITE_MOCK_API so the whole `./mocks`
  // tree (msw + handlers + seeds) is statically dead in a production build and Vite drops it from
  // the bundle entirely. Enabled by VITE_MOCK_API=true (npm run start:mock).
  if (import.meta.env.VITE_MOCK_API === 'true') {
    const {startMockApi} = await import('./mocks/start')
    await startMockApi()
    // `start:v2`: the new experience on for this browser (the `ui_v2_experience` dev flag).
    if (import.meta.env.VITE_V2_MOCK === 'true') {
      const {enableV2AppForDev} = await import('@v2/app/mockStart')
      enableV2AppForDev()
    }
  }
  // The new experience's own API (`/<service>/api/v2/*`, AB#6639): per resource
  // mock or real (src/v2/mocks/mockMode.ts), in every dev build; in a production
  // build only when explicitly allowed. One of the two v1 → v2 mount points.
  if (import.meta.env.DEV || import.meta.env.VITE_V2_ALLOW_MOCKS === 'true') {
    const {startV2Mocks} = await import('@v2/app/mockStart')
    await startV2Mocks()
  }

  // Dev only: `?holdBoot` keeps the boot screen up to review its loader (AB#6639).
  if (import.meta.env.DEV && new URLSearchParams(window.location.search).has('holdBoot')) return

  const rootElement = document.getElementById('root')
  if (!rootElement) {
    throw new Error('Failed to find the root element')
  }
  const root = ReactDOM.createRoot(rootElement)
  root.render(<App />)
}
main()

// If you want your app to work offline and load faster, you can change
// unregister() to register() below. Note this comes with some pitfalls.
// Learn more about service workers: https://bit.ly/CRA-PWA
// serviceWorker.unregister()
